import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { GoogleMap, InfoWindow, MarkerF, useJsApiLoader } from '@react-google-maps/api';
import { buildAddressString, loadGeocodeCache, saveGeocodeCache } from '../utils/geocodeCache';
import './FacilityMapModal.css';

// Modal overlay that plots a set of facilities on a Google Map. Designed
// to be reused from anywhere a list of "selected facilities" exists —
// initially the My Customers cards (click the "N facilities" stat) and
// later the green "View facilities" button on every analysis page.
//
// Inputs:
//   facilities — array of objects with at minimum { id, name }. Optional
//                fields (latitude/longitude, street/city/state/postal/
//                country) drive the marker placement and InfoWindow
//                copy. Items without ANY locatable info are still
//                listed in the empty-state count.
//   title      — what to put in the modal header.
//   subtitle   — optional smaller line under the title (e.g. "Memorial
//                Hermann · 12 facilities").
//   onClose    — handler for the close button + scrim click.
//
// Geocoding strategy:
//   1. If `latitude` + `longitude` are present on a facility row,
//      drop the marker immediately. (Salesforce or our nightly
//      back-fill provided them.)
//   2. Otherwise, lazily geocode the textual address via
//      window.google.maps.Geocoder once the map has loaded. Results
//      are cached in sessionStorage keyed by the address string so a
//      reopen of the modal doesn't pay the API call again.
//   3. Facilities with neither coords nor a recognizable address are
//      shown in the empty-state footer so the user knows why fewer
//      pins appeared than expected.
//
// The Google Maps script is loaded with useJsApiLoader (idempotent —
// safe to call from multiple modal instances; the loader just no-ops
// the second time).

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';

// Static so the loader knows it's the same call across mounts.
// Geometry is needed for fitBounds + lat/lng objects; geocoder is
// loaded explicitly via the Geocoder service.
const LIBRARIES = ['places'];

// Center of the contiguous US — only used as the initial fallback
// when nothing has been geocoded yet (the map fits to bounds as
// soon as we have any markers).
const DEFAULT_CENTER = { lat: 39.8283, lng: -98.5795 };
const DEFAULT_ZOOM = 4;

const MAP_CONTAINER_STYLE = { width: '100%', height: '100%' };

function FacilityMapModal({ facilities = [], title, subtitle, onClose }) {
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: LIBRARIES,
  });

  const [activeMarkerId, setActiveMarkerId] = useState(null);
  const [resolved, setResolved] = useState({}); // facility id -> {lat, lng, source}
  const mapRef = useRef(null);
  // Tracked as state (not just a ref) so the fitBounds effect can
  // depend on it. Without this, the effect can fire before
  // GoogleMap.onLoad has run, skip silently because mapRef.current is
  // null, and never re-fire — leaving the map stuck at the default
  // North-America view even though we have valid points.
  const [mapReady, setMapReady] = useState(false);

  // Shut the modal on Esc to match the rest of the app's dialogs.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose && onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Bucket facilities into "already has coords" / "needs geocoding" /
  // "no address available" so the UI can surface counts and the
  // geocoder loop knows what to work on.
  const buckets = useMemo(() => {
    const withCoords = [];
    const needGeocode = [];
    const noAddress = [];
    for (const f of facilities) {
      const lat = Number.isFinite(f.latitude) ? f.latitude : null;
      const lng = Number.isFinite(f.longitude) ? f.longitude : null;
      if (lat != null && lng != null) {
        withCoords.push({ ...f, _coord: { lat, lng, source: 'salesforce' } });
        continue;
      }
      const addr = buildAddressString(f);
      if (addr) {
        needGeocode.push({ ...f, _address: addr });
      } else {
        noAddress.push(f);
      }
    }
    return { withCoords, needGeocode, noAddress };
  }, [facilities]);

  // Stable signature for the set of addresses that need geocoding,
  // computed off the array contents rather than its reference. This
  // matters because `buckets` is rebuilt every time `facilities`
  // changes reference (which happens whenever the parent re-renders,
  // even if the contents are equal). If the geocoding effect keyed off
  // the array reference itself, every parent re-render would cancel
  // the in-flight Geocoder call and start a new one — infinite loop
  // that never converges, footer permanently stuck on "Geocoding N…".
  const needGeocodeKey = useMemo(
    () => buckets.needGeocode.map(f => `${f.id}|${f._address}`).join('||'),
    [buckets.needGeocode]
  );

  // Track the addresses we've already attempted in THIS modal session
  // so a re-fire of the effect (e.g. after a successful geocode
  // resolution causes a setState → re-render) doesn't loop back over
  // the same items.
  const attemptedRef = useRef(new Set());

  useEffect(() => {
    if (!isLoaded) return;
    if (!needGeocodeKey) return;
    const geocoder = new window.google.maps.Geocoder();
    const cache = loadGeocodeCache();
    let cancelled = false;

    (async () => {
      for (const f of buckets.needGeocode) {
        if (cancelled) return;
        if (attemptedRef.current.has(f._address)) continue;
        attemptedRef.current.add(f._address);

        const cached = cache[f._address];
        if (cached) {
          setResolved(prev => ({ ...prev, [f.id]: { ...cached, source: 'cache' } }));
          continue;
        }
        try {
          const { results } = await geocoder.geocode({ address: f._address });
          if (results && results[0]) {
            const { lat, lng } = results[0].geometry.location.toJSON();
            const entry = { lat, lng };
            cache[f._address] = entry;
            saveGeocodeCache(cache);
            if (!cancelled) {
              setResolved(prev => ({ ...prev, [f.id]: { ...entry, source: 'google' } }));
            }
          }
        } catch {
          // OVER_QUERY_LIMIT or ZERO_RESULTS - silently skip; the
          // backend back-fill will retry next refresh, and we don't
          // want a single bad address to break the whole map.
        }
        // Small per-call delay so we behave nicely with the free tier.
        await new Promise(r => setTimeout(r, 60));
      }
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, needGeocodeKey]);

  // Final list of points to plot = pre-resolved coords + lazily-resolved
  // geocoded coords. Memoized off [buckets, resolved] so adding a new
  // marker doesn't re-render every existing marker.
  const points = useMemo(() => {
    const out = buckets.withCoords.map(f => ({
      id: f.id,
      name: f.name,
      addressLines: [
        f.street,
        [f.city, f.state, f.postalCode].filter(Boolean).join(', '),
        f.country,
      ].filter(Boolean),
      lat: f._coord.lat,
      lng: f._coord.lng,
      source: f._coord.source,
    }));
    for (const f of buckets.needGeocode) {
      const r = resolved[f.id];
      if (!r) continue;
      out.push({
        id: f.id,
        name: f.name,
        addressLines: [
          f.street,
          [f.city, f.state, f.postalCode].filter(Boolean).join(', '),
          f.country,
        ].filter(Boolean),
        lat: r.lat,
        lng: r.lng,
        source: r.source,
      });
    }
    return out;
  }, [buckets, resolved]);

  // Stable signature for the plotted points so re-renders that don't
  // change the point set don't re-trigger fitBounds (which calls into
  // Google Maps and fires `bounds_changed`/`idle` events that can race
  // with React state updates).
  const pointsKey = useMemo(
    () => points.map(p => `${p.id}:${p.lat.toFixed(5)},${p.lng.toFixed(5)}`).join('|'),
    [points]
  );

  useEffect(() => {
    if (!mapReady || !mapRef.current || points.length === 0) return;
    if (!window.google?.maps) return;
    if (points.length === 1) {
      mapRef.current.setCenter({ lat: points[0].lat, lng: points[0].lng });
      mapRef.current.setZoom(11);
      return;
    }
    const bounds = new window.google.maps.LatLngBounds();
    points.forEach(p => bounds.extend({ lat: p.lat, lng: p.lng }));
    // Pad so markers near the edges aren't clipped by the InfoWindow
    // anchor; 64px gives a comfortable gutter on the modal-sized map.
    mapRef.current.fitBounds(bounds, 64);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pointsKey, mapReady]);

  const onMapLoad = (map) => {
    mapRef.current = map;
    setMapReady(true);
  };
  const onMapUnmount = () => {
    mapRef.current = null;
    setMapReady(false);
  };

  const pendingCount = buckets.needGeocode.length - Object.keys(resolved).length;

  // Stop click events on the modal card from bubbling up to the
  // backdrop (which would otherwise close the modal mid-interaction).
  const onCardClick = (e) => e.stopPropagation();

  // Render-via-portal so the modal escapes the My Customers card's
  // ancestor chain. The card has `transform: translateY(-2px)` on
  // hover which retargets `position: fixed` to the card itself
  // instead of the viewport — that bug used to cause the modal to
  // jitter as the underlying card's hover state oscillated. Mounting
  // straight onto document.body sidesteps the issue and also stops
  // clicks on the backdrop from bubbling to the card's onClick
  // (which would otherwise navigate to /customer-data).
  const noAddressCount = buckets.noAddress.length;
  const totalUnplottable = noAddressCount +
    (buckets.needGeocode.length - Object.keys(resolved).length);

  let bodyContent;
  if (!GOOGLE_MAPS_API_KEY) {
    bodyContent = (
      <div className="facility-map-modal-error">
        <strong>Map unavailable</strong>
        <span>
          <code>REACT_APP_GOOGLE_MAPS_API_KEY</code> isn't set on
          this build. Add it to <code>.env</code> and rebuild the
          frontend container to enable map views.
        </span>
      </div>
    );
  } else if (loadError) {
    bodyContent = (
      <div className="facility-map-modal-error">
        <strong>Map failed to load</strong>
        <span>Check the Google Cloud project — the Maps JavaScript API may not be enabled, or this domain isn't in the API key's referrer list.</span>
      </div>
    );
  } else if (!isLoaded) {
    bodyContent = <div className="facility-map-modal-loading">Loading map…</div>;
  } else if (facilities.length === 0) {
    bodyContent = (
      <div className="facility-map-modal-empty">
        <strong>No facilities to plot</strong>
        <span>This customer doesn't have any facilities resolved yet.</span>
      </div>
    );
  } else {
    bodyContent = (
      <>
        <GoogleMap
          mapContainerStyle={MAP_CONTAINER_STYLE}
          center={DEFAULT_CENTER}
          zoom={DEFAULT_ZOOM}
          onLoad={onMapLoad}
          onUnmount={onMapUnmount}
          options={{
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: true,
          }}
        >
          {points.map(p => (
            <MarkerF
              key={p.id}
              position={{ lat: p.lat, lng: p.lng }}
              onClick={() => setActiveMarkerId(p.id)}
            />
          ))}
          {activeMarkerId && (() => {
            const p = points.find(x => x.id === activeMarkerId);
            if (!p) return null;
            return (
              <InfoWindow
                position={{ lat: p.lat, lng: p.lng }}
                onCloseClick={() => setActiveMarkerId(null)}
              >
                <div className="facility-map-marker-card">
                  <div className="facility-map-marker-card-name">{p.name}</div>
                  <div className="facility-map-marker-card-addr">
                    {p.addressLines.length > 0 ? p.addressLines.join('\n') : 'No address on file'}
                  </div>
                </div>
              </InfoWindow>
            );
          })()}
        </GoogleMap>

        {/* When the customer has facilities but none of them have
            usable address data yet (common right after the address
            columns are added — Salesforce hasn't been re-synced), the
            map sits empty over the default US-wide view. Surface a
            clear callout so the user knows what to do, instead of
            staring at a blank ocean. */}
        {points.length === 0 && totalUnplottable === facilities.length && (
          <div className="facility-map-modal-empty">
            <strong>No facility addresses cached yet</strong>
            <span>
              Run <em>Refresh from Salesforce</em> on the Refresh page —
              the new address columns get populated then, and the
              backend back-fills any missing coordinates from Google
              Geocoding automatically.
            </span>
          </div>
        )}
      </>
    );
  }

  const modalNode = (
    <div className="facility-map-modal-backdrop" onMouseDown={onClose}>
      {/* onMouseDown (not onClick) on the backdrop avoids the
          flicker that happens when a child button consumes the
          mouseup but the click still synthesizes here. The card
          stops propagation so clicks inside the modal stay inside. */}
      <div
        className="facility-map-modal-card"
        onMouseDown={onCardClick}
        onClick={onCardClick}
      >
        <Header title={title} subtitle={subtitle} onClose={onClose} />
        <div className="facility-map-modal-body">{bodyContent}</div>
        <div className="facility-map-modal-footer">
          <div className="facility-map-modal-legend">
            <span><span className="facility-map-modal-legend-dot salesforce" />Plotted: {points.length}</span>
            {pendingCount > 0 && (
              <span><span className="facility-map-modal-legend-dot geocoded" />Geocoding {pendingCount}…</span>
            )}
            {buckets.noAddress.length > 0 && (
              <span><span className="facility-map-modal-legend-dot unknown" />No address: {buckets.noAddress.length}</span>
            )}
          </div>
          <span>{facilities.length} facilit{facilities.length === 1 ? 'y' : 'ies'} total</span>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalNode, document.body);
}

function Header({ title, subtitle, onClose }) {
  return (
    <div className="facility-map-modal-header">
      <div className="facility-map-modal-title">
        <span>{title || 'Facility map'}</span>
        {subtitle && <span className="facility-map-modal-subtitle">{subtitle}</span>}
      </div>
      <button type="button" className="facility-map-modal-close" onClick={onClose}>Close</button>
    </div>
  );
}

export default FacilityMapModal;
